import Add from '@mui/icons-material/Add';
import { Box, Button, Grid, IconButton, Skeleton, Typography } from '@mui/material';
import React from 'react';
import CommonInput from '../../component/form/common-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import DialogComponent from '../../component/dialog/dialog.component';
import _ from 'lodash';
import { apiManage_rbp as apiManage } from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import './scss/print-head.styles.scss';
import { PrintModuleAuxiliaryModeContext } from './print-module-auxiliary-mode-context';
import {
    PrintDetailDeletePrinterButton,
    PrintDetailHeadActions,
    PrintDetailSaveButton,
} from './components/print-detail-head-buttons';

enum PrintHeadDialogType {
    ADD_PRINT_HEAD = 'ADD_PRINT_HEAD',
}

class PrintHeadPage extends React.Component<Props, State> {
    static contextType = PrintModuleAuxiliaryModeContext;
    context!: React.ContextType<typeof PrintModuleAuxiliaryModeContext>;

    constructor(props: Props) {
        super(props);

        this.onSave = _.debounce(this.onSave, 500, {
            leading: true,
            trailing: false,
        }).bind(this) as any;
    }

    public state: State = {
        printHeads: [],
        activeMenu: '',
        printHeadName: '',
        printHeadDialog: false,
        printHeadDialogType: undefined,
        printHeadData: [],
        addPrintHeadName: '',
        pageLoading: true,
        deleteAlertOpen: false,
        deleteTarget: null as any,
        deleteSubmitting: false,
        saveSubmitting: false,
        dialogConfirmLoading: false,
    };

    componentDidMount() {
        this.getPrinterHeads();
    }

    /**
     * 获取打印头列表
     */
    async getPrinterHeads() {
        try {
            const res = await apiManage.getPrinterHead();
            if (!res?.error && Array.isArray(res)) {
                const printHeads = res.map((item: any) => ({
                    title: item.descriptors_headers_name,
                    key: `${item.descriptors_headers_id}`,
                    dataSource: item,
                }));
                const activeDeviceItem = res.find(
                    (item: any) => `${item.descriptors_headers_id}` === `${this.state.activeMenu}`,
                );
                const deviceItem = activeDeviceItem ? activeDeviceItem : res[0];
                const emptyLines = () => [
                    { value: '' },
                    { value: '' },
                    { value: '' },
                    { value: '' },
                    { value: '' },
                    { value: '' },
                ];
                this.setState({
                    printHeads,
                    activeMenu: deviceItem?.descriptors_headers_id != null ? `${deviceItem.descriptors_headers_id}` : '',
                });
                if (deviceItem) {
                    this.getPrinterHeadItem(deviceItem);
                } else {
                    this.setState({
                        printHeadName: '',
                        printHeadData: emptyLines(),
                    });
                }
            }
        } finally {
            this.setState({ pageLoading: false });
        }
    }

    /**
     * 选中单个打印头，填充表单
     */
    getPrinterHeadItem(item: any) {
        const printHeadData = [
            { value: item?.line_1 || '' },
            { value: item?.line_2 || '' },
            { value: item?.line_3 || '' },
            { value: item?.line_4 || '' },
            { value: item?.line_5 || '' },
            { value: item?.line_6 || '' },
        ];

        this.setState({
            printHeadData,
            printHeadName: item?.descriptors_headers_name,
            activeMenu: `${item?.descriptors_headers_id ?? ''}`,
        });
    }

    /** 顶部「+」打开新增弹窗 */
    openAddPrintHeadDialog = () => {
        this.setState({
            printHeadDialog: true,
            printHeadDialogType: PrintHeadDialogType.ADD_PRINT_HEAD,
            addPrintHeadName: '',
        });
    };

    /** 详情头删除：与列表「更多」删除共用同一确认框 */
    openDeletePrintHeadFromDetail = () => {
        const row = this.state.printHeads.find((r) => String(r.key) === String(this.state.activeMenu));
        if (!row?.dataSource) {
            return;
        }
        this.setState({ deleteAlertOpen: true, deleteTarget: row.dataSource });
    };

    /** 行内删除：公共 AlertDialog 确认后提交 */
    confirmDeletePrintHead = async () => {
        const id = this.state.deleteTarget?.descriptors_headers_id;
        if (id == null) {
            return;
        }
        this.setState({ deleteSubmitting: true });
        try {
            await apiManage.delPrinterHead([Number(id)]);
            this.setState({ deleteAlertOpen: false, deleteTarget: null });
            await this.getPrinterHeads();
        } finally {
            this.setState({ deleteSubmitting: false });
        }
    };

    dialogConfig(action: 'CONTENT' | 'TITLE' | 'FUN' = 'FUN') {
        const { printHeadDialogType } = this.state;
        let content = <div />;
        let fun = async () => {};
        let title = '';
        switch (printHeadDialogType) {
            case PrintHeadDialogType.ADD_PRINT_HEAD:
                if (action === 'CONTENT') {
                    content = (
                        <FormFieldCell labelKey="print_head_page_label">
                            <CommonInput
                                variant="outlined"
                                fullWidth
                                hiddenLabel
                                regularType="required"
                                value={this.state.addPrintHeadName}
                                onChange={(e) => {
                                    this.setState({ addPrintHeadName: e.target.value });
                                }}
                            />
                        </FormFieldCell>
                    );
                } else if (action === 'TITLE') {
                    title = i18n.t('print_head_page_info');
                } else {
                    fun = async () => {
                        if (_.trim(this.state.addPrintHeadName) === '') {
                            GlobalNotification.warning(i18n.t('staff_setting_page_enter_type_name'));
                            return;
                        }
                        await apiManage.postPrinterHead({ descriptors_headers_name: this.state.addPrintHeadName });
                        this.setState({
                            printHeadDialog: false,
                            printHeadDialogType: undefined,
                        });
                        this.getPrinterHeads();
                    };
                }
                break;
            default:
                break;
        }
        if (action === 'CONTENT') {
            return content;
        }
        if (action === 'TITLE') {
            return title;
        }
        return fun;
    }

    /**
     * 保存当前打印头
     */
    async onSave() {
        const { activeMenu, printHeadData, printHeadName } = this.state;
        if (_.trim(printHeadName) === '') {
            return GlobalNotification.error(i18n.t('staff_setting_page_enter_type_name'));
        }
        this.setState({ saveSubmitting: true });
        try {
            const res = await apiManage.patchPrinterHead(activeMenu, {
                descriptors_headers_name: printHeadName,
                line_1: printHeadData?.[0]?.value || '',
                line_2: printHeadData?.[1]?.value || '',
                line_3: printHeadData?.[2]?.value || '',
                line_4: printHeadData?.[3]?.value || '',
                line_5: printHeadData?.[4]?.value || '',
                line_6: printHeadData?.[5]?.value || '',
            });
            if (!res?.error) {
                this.getPrinterHeads();
                GlobalNotification.success(i18n.t('google_text_edit_succeed'));
            }
        } finally {
            this.setState({ saveSubmitting: false });
        }
    }

    render() {
        const {
            printHeads,
            activeMenu,
            printHeadDialog,
            printHeadName,
            printHeadData,
            pageLoading,
            deleteAlertOpen,
            deleteSubmitting,
            saveSubmitting,
            dialogConfirmLoading,
        } = this.state;

        if (pageLoading) {
            return <PrintHeadPageSkeleton />;
        }

        const nameFieldHint = i18n.t('print_native_protocol_page_param_hint');

        return (
            <Box className="print-head-page print-head-page--shell">
                <Grid className="print-head-page__layout" container spacing={3}>
                    <Grid item className="print-head-page__list-column">
                        <Box className="print-head-page__list-card">
                            <Box className="print-head-page__list-head">
                                <Typography component="div" className="print-head-page__list-title">
                                    {i18n.t('print_head_page_label')}
                                </Typography>
                                <IconButton
                                    size="small"
                                    className="print-head-page__list-add"
                                    onClick={() => {
                                        this.openAddPrintHeadDialog();
                                    }}
                                    aria-label={i18n.t('google_text_newly_increased')}
                                >
                                    <Add />
                                </IconButton>
                            </Box>
                            <Box className="print-head-page__list-body">
                                {printHeads.map((row: any) => {
                                    const isActive = String(activeMenu) === String(row.key);
                                    return (
                                        <Box
                                            key={row.key}
                                            className={
                                                isActive
                                                    ? 'print-head-page__list-item print-head-page__list-item--active'
                                                    : 'print-head-page__list-item'
                                            }
                                            onClick={() => {
                                                this.getPrinterHeadItem(row.dataSource);
                                            }}
                                        >
                                            <Box className="print-head-page__list-item-text">
                                                <Typography component="div" className="print-head-page__list-item-name">
                                                    {row.title}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Grid>
                    {activeMenu ? (
                        <Grid item xs className="print-head-page__detail-column" minWidth={0}>
                            <Box className="print-head-page__detail-card">
                                <Box className="print-head-page__detail-head">
                                    <Typography component="div" className="print-head-page__detail-title">
                                        {i18n.t('print_head_page_setting')}
                                    </Typography>
                                    <PrintDetailHeadActions className="print-head-page__detail-actions">
                                        <PrintDetailDeletePrinterButton
                                            className="print-head-page__btn-delete-printer"
                                            label={i18n.t('print_detail_delete_print_head')}
                                            disabled={!activeMenu}
                                            onClick={() => this.openDeletePrintHeadFromDetail()}
                                        />
                                        <PrintDetailSaveButton
                                            className="print-head-page__btn-save"
                                            loading={saveSubmitting}
                                            onClick={() => void this.onSave()}
                                        >
                                            {i18n.t('job_settings_save')}
                                        </PrintDetailSaveButton>
                                    </PrintDetailHeadActions>
                                </Box>
                                <Box className="print-head-page__detail-body">
                                    <Box className="print-head-page__field">
                                        <Typography component="div" className="print-head-page__field-label">
                                            {i18n.t('global_text_name')}
                                        </Typography>
                                        {this.context?.auxiliaryModeEnabled ? (
                                            <Typography component="div" className="print-head-page__field-hint">
                                                {nameFieldHint}
                                            </Typography>
                                        ) : null}
                                        <CommonInput
                                            variant="outlined"
                                            fullWidth
                                            hiddenLabel
                                            value={printHeadName}
                                            onChange={(e) => {
                                                this.setState({ printHeadName: e.target.value });
                                            }}
                                        />
                                    </Box>
                                    {printHeadData.map((item, index) => (
                                        <Box className="print-head-page__field" key={index}>
                                            <Typography component="div" className="print-head-page__field-label">
                                                {`${i18n.t('print_tail_page_row')} ${index + 1}`}
                                            </Typography>
                                            {index === 0 && this.context?.auxiliaryModeEnabled ? (
                                                <Typography component="div" className="print-head-page__field-hint">
                                                    {i18n.t('print_head_page_general_restaurant_name')}
                                                </Typography>
                                            ) : null}
                                            <CommonInput
                                                variant="outlined"
                                                fullWidth
                                                hiddenLabel
                                                value={item.value}
                                                onChange={(e) => {
                                                    const newPrintHeadData = _.cloneDeep(printHeadData);
                                                    newPrintHeadData[index].value = e.target.value;
                                                    this.setState({ printHeadData: newPrintHeadData });
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    ) : (
                        <Grid item xs className="print-head-page__detail-column" minWidth={0}>
                            <Box className="print-head-page__detail-card">
                                <Typography className="print-head-page__empty" component="div">
                                    {i18n.t('google_text_public_text_no_data')}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
                <AlertDialog
                    open={deleteAlertOpen}
                    variant="delete"
                    title={i18n.t('dinner_table_page_confirm_delete')}
                    desc={i18n.t('dinner_table_page_whether_delete')}
                    size="xs"
                    confirmLoading={deleteSubmitting}
                    onCancel={() => {
                        this.setState({ deleteAlertOpen: false, deleteTarget: null });
                    }}
                    onConfirm={() => void this.confirmDeletePrintHead()}
                />
                <DialogComponent
                    open={printHeadDialog}
                    fullWidth={false}
                    onClose={() => {
                        if (!this.state.dialogConfirmLoading) {
                            this.setState({ printHeadDialog: false });
                        }
                    }}
                    defaultHeaderProps={{
                        title: this.dialogConfig('TITLE') as string,
                        onClose: () => {
                            if (!this.state.dialogConfirmLoading) {
                                this.setState({ printHeadDialog: false });
                            }
                        },
                    }}
                    footer={
                        <div>
                            <Button
                                color="primary"
                                variant="outlined"
                                disabled={dialogConfirmLoading}
                                onClick={() => {
                                    this.setState({ printHeadDialog: false });
                                }}
                            >
                                {i18n.t('google_text_public_button_cancel')}
                            </Button>
                            <Box marginLeft={4} display="inline-block">
                                <LoadingOverlayButton
                                    type="button"
                                    color="primary"
                                    variant="contained"
                                    loading={dialogConfirmLoading}
                                    loadingIndicatorSize={22}
                                    onClick={async () => {
                                        this.setState({ dialogConfirmLoading: true });
                                        try {
                                            const fun = this.dialogConfig();
                                            if (typeof fun === 'function') {
                                                await fun();
                                            }
                                        } finally {
                                            this.setState({ dialogConfirmLoading: false });
                                        }
                                    }}
                                >
                                    {i18n.t('google_text_confirm')}
                                </LoadingOverlayButton>
                            </Box>
                        </div>
                    }
                >
                    <Grid style={{ padding: '8px 8px' }}>{this.dialogConfig('CONTENT') as React.ReactNode}</Grid>
                </DialogComponent>
            </Box>
        );
    }
}

function PrintHeadPageSkeleton() {
    return (
        <Box className="print-head-page print-head-page--shell print-head-page--skeleton">
            <Grid className="print-head-page__layout" container spacing={3}>
                <Grid item className="print-head-page__list-column">
                    <Box className="print-head-page__list-card">
                        <Box className="print-head-page__list-head">
                            <Skeleton className="print-head-page__skeleton-title" variant="text" width={160} height={28} />
                            <Skeleton variant="circular" width={32} height={32} />
                        </Box>
                        <Box className="print-head-page__list-body print-head-page__skeleton-list">
                            {[1, 2, 3, 4, 5, 6].map((k) => (
                                <Skeleton key={k} className="print-head-page__skeleton-list-row" variant="rounded" height={72} />
                            ))}
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs className="print-head-page__detail-column" minWidth={0}>
                    <Box className="print-head-page__detail-card">
                        <Box className="print-head-page__detail-head">
                            <Skeleton variant="text" width={180} height={28} />
                            <Box display="flex" sx={{ gap: 3 }}>
                                <Skeleton variant="rounded" width={120} height={44} />
                                <Skeleton variant="rounded" width={140} height={44} />
                            </Box>
                        </Box>
                        <Box className="print-head-page__detail-body">
                            <Box className="print-head-page__field">
                                <Skeleton variant="text" width="35%" height={22} />
                                <Skeleton variant="text" width="55%" height={20} sx={{ mt: 0.5 }} />
                                <Skeleton variant="rounded" width="100%" height={56} sx={{ maxWidth: 400, mt: 1 }} />
                            </Box>
                            {[1, 2, 3].map((i) => (
                                <Box key={i} className="print-head-page__field">
                                    <Skeleton variant="text" width="28%" height={22} />
                                    <Skeleton variant="rounded" width="100%" height={56} sx={{ maxWidth: 400, mt: 1 }} />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default PrintHeadPage;

type State = {
    printHeads: any[];
    printHeadName: string;
    printHeadDialog: boolean;
    printHeadDialogType: PrintHeadDialogType | undefined;
    activeMenu: string;
    printHeadData: { value: string }[];
    addPrintHeadName: string;
    pageLoading: boolean;
    deleteAlertOpen: boolean;
    deleteTarget: any;
    deleteSubmitting: boolean;
    saveSubmitting: boolean;
    dialogConfirmLoading: boolean;
};

type Props = Record<string, never>;
